import React, { useState, useEffect, useCallback } from "react";
import { useExcalidrawAPI } from "@excalidraw/excalidraw";
import { ToolButton } from "@excalidraw/excalidraw/components/ToolButton";

const API_BASE = "https://whiteboard.forexterminal-ai.com/api";

export const WorkspaceManager = () => {
  const excalidrawAPI = useExcalidrawAPI();
  const [token, setToken] = useState<string | null>(null);
  const [username, setUsername] = useState<string | null>(null);

  useEffect(() => {
    // Check if we're in embed mode (token/username set by embed.php)
    const wbUser = (window as any).__WB_USER;
    const wbToken = (window as any).__WB_TOKEN;
    if (wbUser && wbToken) {
      setUsername(wbUser);
      setToken(wbToken);
    }
  }, []);

  const setStatus = useCallback(
    (msg: string, isErr?: boolean) => {
      excalidrawAPI?.setToast({
        message: msg,
        duration: 3000,
      });
    },
    [excalidrawAPI],
  );

  const handleSave = useCallback(async () => {
    if (!excalidrawAPI || !token) return;
    const title = prompt("Save drawing as:", "My Drawing");
    if (!title) return;

    try {
      const elements = excalidrawAPI.getSceneElements();
      const appState = excalidrawAPI.getAppState();
      const files = excalidrawAPI.getFiles();

      const scene = JSON.stringify({
        type: "excalidraw",
        elements,
        appState,
        files,
        version: 2,
      });
      const encoded = btoa(unescape(encodeURIComponent(scene)));

      const res = await fetch(`${API_BASE}/save.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ scene: encoded, title, room_id: "" }),
      });
      const result = await res.json();
      if (result.success) {
        setStatus(`✅ Saved "${title}"`);
      } else {
        setStatus(`❌ ${result.error || "Save failed"}`, true);
      }
    } catch (e: any) {
      setStatus(`❌ Error: ${e.message}`, true);
    }
  }, [excalidrawAPI, token, setStatus]);

  const handleLoad = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_BASE}/mydrawings.php`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const drawings = await res.json();
      if (!drawings || drawings.length === 0) {
        setStatus("No saved drawings yet", true);
        return;
      }

      // Show a simple prompt-based picker (first 5)
      let msg = "Your drawings:\n";
      for (let i = 0; i < Math.min(drawings.length, 10); i++) {
        msg += `${i + 1}. ${drawings[i].title || "Untitled"} (${(drawings[i].updated_at || "").substring(0, 10)})\n`;
      }
      const choice = prompt(msg + "\nEnter number to load (or cancel)");
      if (!choice) return;
      const idx = parseInt(choice, 10) - 1;
      if (idx < 0 || idx >= drawings.length) {
        setStatus("Invalid choice", true);
        return;
      }

      const drawingId = drawings[idx].id;
      const loadRes = await fetch(`${API_BASE}/load.php?id=${drawingId}`);
      if (!loadRes.ok) {
        setStatus("Not found", true);
        return;
      }
      const text = await loadRes.text();
      let data: any = null;
      try {
        data = JSON.parse(text);
      } catch (e) {}
      if (!data) {
        try {
          data = JSON.parse(decodeURIComponent(escape(atob(text))));
        } catch (e) {}
      }
      if (data && data.elements) {
        excalidrawAPI?.updateScene({
          elements: data.elements,
          appState: data.appState || {},
        });
        setStatus(`✅ Loaded "${drawings[idx].title}"`);
      } else {
        setStatus("Invalid drawing data", true);
      }
    } catch (e: any) {
      setStatus(`❌ Error: ${e.message}`, true);
    }
  }, [excalidrawAPI, token, setStatus]);

  // Don't show unless we're in embed mode with token
  if (!token || !username) return null;

  return (
    <div
      style={{
        position: "fixed",
        bottom: 12,
        left: 12,
        zIndex: 999999,
        display: "flex",
        gap: 6,
        alignItems: "center",
      }}
    >
      <ToolButton
        type="button"
        title="Save to your workspace"
        aria-label="Save to workspace"
        onClick={handleSave}
        style={{
          padding: "6px 14px",
          fontSize: 12,
          background: "#0ea5e9",
          color: "#fff",
          border: "none",
          borderRadius: 6,
          cursor: "pointer",
          fontWeight: 600,
          boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
        }}
      >
        💾 Save
      </ToolButton>
      <ToolButton
        type="button"
        title="Load from your workspace"
        aria-label="Load from workspace"
        onClick={handleLoad}
        style={{
          padding: "6px 14px",
          fontSize: 12,
          background: "#1a2332",
          color: "#e2e8f0",
          border: "1px solid #1e3a5f",
          borderRadius: 6,
          cursor: "pointer",
          fontWeight: 600,
          boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
        }}
      >
        📂 Load
      </ToolButton>
      <span
        style={{
          fontSize: 10,
          color: "#64748b",
          fontFamily: "JetBrains Mono, monospace",
          marginLeft: 4,
        }}
      >
        {username}
      </span>
    </div>
  );
};
